import { useQueries } from "react-query";
import { LOCALES, healthFormsApi } from "./api";
import type { PreviewAttrs, PreviewLocale, PatientPreviewResponse } from "./api";
import { queryKeys } from "./queries";
import { serializeResolvedQuestionnaire } from "./serializeResolved";
import { buildRows, countChanges } from "./diffRows";

export type SnapshotRef = { kind: "draft" } | { kind: "published"; id: number };

export const refKey = (r: SnapshotRef) => (r.kind === "draft" ? "draft" : String(r.id));

export const parseRef = (raw: string | null): SnapshotRef | null => {
  if (!raw) return null;
  if (raw === "draft") return { kind: "draft" };
  if (!/^\d+$/.test(raw)) return null;
  const n = parseInt(raw, 10);
  return n > 0 ? { kind: "published", id: n } : null;
};

export type LocaleInfo = {
  locale: PreviewLocale;
  count: number | null;
  isLoading: boolean;
  before: PatientPreviewResponse | null;
  after: PatientPreviewResponse | null;
};

type SideQuery = {
  queryKey: readonly unknown[];
  queryFn: () => Promise<PatientPreviewResponse>;
  enabled: boolean;
};

const sideQuery = (
  ref: SnapshotRef | null,
  draftId: number | null,
  attrs: PreviewAttrs,
): SideQuery => {
  if (ref?.kind === "draft" && draftId) {
    return {
      queryKey: queryKeys.questionnairePreview(draftId, attrs),
      queryFn: () => healthFormsApi.questionnaires.preview(draftId, attrs),
      enabled: true,
    };
  }
  if (ref?.kind === "published") {
    return {
      queryKey: queryKeys.publishedQuestionnairePreview(ref.id, attrs),
      queryFn: () => healthFormsApi.publishedQuestionnaires.preview(ref.id, attrs),
      enabled: true,
    };
  }
  return {
    queryKey: ["healthForms", "diff", "noop"] as const,
    queryFn: () => Promise.reject(new Error("no snapshot")),
    enabled: false,
  };
};

const computeLocaleInfo = (
  locale: PreviewLocale,
  before: PatientPreviewResponse | undefined,
  after: PatientPreviewResponse | undefined,
  isLoading: boolean,
): LocaleInfo => {
  if (isLoading) return { locale, count: null, isLoading, before: null, after: null };
  if (!before || !after) return { locale, count: null, isLoading, before: before ?? null, after: after ?? null };
  const oldStr = serializeResolvedQuestionnaire(before.questionnaire);
  const newStr = serializeResolvedQuestionnaire(after.questionnaire);
  return { locale, count: countChanges(buildRows(oldStr, newStr)), isLoading, before, after };
};

export const formatLocaleLabel = (locale: PreviewLocale, info: LocaleInfo | undefined): string => {
  if (!info || info.count === null) return `${locale} (…)`;
  return `${locale} (${info.count})`;
};

export const useLocaleDiffPreviews = ({
  before,
  after,
  draftId,
  attrs,
}: {
  before: SnapshotRef | null;
  after: SnapshotRef | null;
  draftId: number | null;
  attrs: PreviewAttrs;
}): {
  localeInfo: LocaleInfo[];
  current: LocaleInfo;
  previewsLoading: boolean;
  anyError: boolean;
} => {
  const queries = useQueries(
    LOCALES.flatMap((locale) => {
      const localeAttrs = { ...attrs, locale };
      return [
        sideQuery(before, draftId, localeAttrs),
        sideQuery(after, draftId, localeAttrs),
      ];
    }),
  );

  const localeInfo: LocaleInfo[] = LOCALES.map((locale, idx) => {
    const beforeQ = queries[idx * 2];
    const afterQ = queries[idx * 2 + 1];
    const isLoading = beforeQ.isFetching || afterQ.isFetching;
    return computeLocaleInfo(
      locale,
      beforeQ.data as PatientPreviewResponse | undefined,
      afterQ.data as PatientPreviewResponse | undefined,
      isLoading,
    );
  });

  const current = localeInfo.find((l) => l.locale === attrs.locale) ?? localeInfo[0];
  const previewsLoading = current.isLoading;
  const anyError = queries.some((q) => q.error);

  return { localeInfo, current, previewsLoading, anyError };
};
